import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Profile, UserPreferences, ApiResponse, ProfileData, CSProfileData, ShiftReport } from '@/types'
import { RootState } from '@/app/store'
import brandManager from '@/services/brandManager'
import { buildEndpoint, getPlatformHeader } from '@/utils/apiUtils'
import { getAuthToken } from '@/utils/secureStorage'

// Helper function to unwrap API response
function unwrapResponse(response: any): any {
  if (response && typeof response === 'object' && 'data' in response && 'message' in response) {
    return response.data
  }
  return response
}

// Transform API response to match ProfileData or CSProfileData structure
function transformProfileResponse(data: any): ProfileData | CSProfileData {
  if (!data) return data

  // Check if this is a customer service profile (has assigned_branches in employee)
  const isCS = data.employee?.assigned_branches && Array.isArray(data.employee.assigned_branches)
  
  // Transform best selling products - using correct API keys
  const bestSellingProducts = data.best_selling_products?.products?.map((product: any) => ({
    productName: product.productName || product.product_name || '',
    productImage: product.productImage || product.product_image || '',
    totalQuantity: parseInt(product.totalQuantity || product.total_quantity || '0', 10),
  })) || []

  // Transform sales summary and sales - prefer explicit summary but fall back to shift details
  const salesSummary = data.sales_summary || data.shift_details || {}
  const sales = data.sales || {}

  if (isCS) {
    // Customer Service Profile
    const csProfile: CSProfileData = {
      employeeName: data.employee?.name || '',
      totalSales: String(salesSummary.total_sales || 0),
      totalOrders: salesSummary.total_orders || 0,
      totalProducts: salesSummary.total_products || 0,
      cashSales: String(sales.cash?.paid_cash_orders_sum || 0),
      cashOrdersCount: sales.cash?.paid_cash_orders_count || 0,
      onlineSales: String(sales.network?.paid_network_orders_sum || sales.online?.paid_online_orders_sum || 0),
      onlineOrdersCount: sales.network?.paid_network_orders_count || sales.online?.paid_online_orders_count || 0,
      walletSales: String(sales.partial?.paid_partial_orders_sum || sales.wallet?.paid_wallet_orders_sum || 0),
      walletOrdersCount: sales.partial?.paid_partial_orders_count || sales.wallet?.paid_wallet_orders_count || 0,
      bestSellingProducts: bestSellingProducts,
      assignedBranches: data.employee?.assigned_branches || [],
      employeeType: data.employee?.type || ''
    }
    return csProfile
  } else {
    // Cashier Profile - Transform shifts to match ShiftReport interface
    const transformedShifts: ShiftReport[] = (data.shifts || []).map((shift: any) => ({
      id: String(shift.id || ''),
      startedAt: shift.started_at || '',
      endedAt: shift.ended_at || undefined,
      totalSales: String(shift.balance || 0), // balance represents total sales for the shift
      totalOrders: 0, // Not provided in API response
      cashSales: '0', // Not provided in API response
      networkSales: '0', // Not provided in API response
      partialSales: '0', // Not provided in API response
      openingAmount: shift.opening_balance || '0',
      closingAmount: shift.closing_balance || undefined,
      enteredClosingBalance: shift.entered_closing_balance || undefined,
      status: shift.status === 'active' ? 'active' : shift.status === 'closed' ? 'completed' : 'pending'
    }))

    const profile: ProfileData = {
      employeeName: data.employee?.name || '',
      totalSales: String(salesSummary.total_sales || 0),
      totalOrders: salesSummary.total_orders || 0,
      totalProducts: salesSummary.total_products || 0,
      cashSales: String(sales.cash?.paid_cash_orders_sum || 0),
      cashOrdersCount: sales.cash?.paid_cash_orders_count || 0,
      networkSales: String(sales.network?.paid_network_orders_sum || 0),
      networkOrdersCount: sales.network?.paid_network_orders_count || 0,
      partialSales: String(sales.partial?.paid_partial_orders_sum || 0),
      partialOrdersCount: sales.partial?.paid_partial_orders_count || 0,
      bestSellingProducts: bestSellingProducts,
      shifts: transformedShifts
    }
    return profile
  }
}

// Base API configuration
const baseQuery = fetchBaseQuery({
  baseUrl: brandManager.apiUrl,
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    const language = (getState() as RootState).settings.settings.language
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    
    headers.set('lang', language)
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    headers.set('platform', getPlatformHeader())
    
    return headers
  },
})

// Profile API slice
export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery,
  tagTypes: ['Profile'],
  endpoints: (builder) => ({
    // Get cashier profile
    getProfile: builder.query<ProfileData | CSProfileData, { startDate?: string; endDate?: string } | void>({
      query: (params) => {
        let url = buildEndpoint('/cashier/profile/')
        if (params && typeof params === 'object' && (params.startDate || params.endDate)) {
          const searchParams = new URLSearchParams()
          if (params.startDate) searchParams.append('start_date', params.startDate)
          if (params.endDate) searchParams.append('end_date', params.endDate)
          if (searchParams.toString()) {
            url += `?${searchParams.toString()}`
          }
        }
        return url
      },
      transformResponse: (response: any) => {
        const unwrapped = unwrapResponse(response)
        return transformProfileResponse(unwrapped)
      },
      providesTags: ['Profile'],
    }),
    
    // Update profile
    updateProfile: builder.mutation<ApiResponse<Profile>, Partial<Profile>>({
      query: (profileData) => ({
        url: '/user/profile',
        method: 'PUT',
        body: profileData,
      }),
      invalidatesTags: ['Profile'],
    }),
    
    // Update preferences
    updatePreferences: builder.mutation<ApiResponse<Profile>, UserPreferences>({
      query: (preferences) => ({
        url: '/user/preferences',
        method: 'PUT',
        body: preferences,
      }),
      invalidatesTags: ['Profile'],
    }),
    
    // Upload avatar
    uploadAvatar: builder.mutation<ApiResponse<{ avatar_url: string }>, FormData>({
      query: (formData) => ({
        url: '/user/avatar',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Profile'],
    }),
    
    // Change password
    changePassword: builder.mutation<ApiResponse<null>, { currentPassword: string; newPassword: string }>({
      query: (passwords) => ({
        url: '/user/change-password',
        method: 'POST',
        body: passwords,
      }),
    }),
  }),
})

export const {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUpdatePreferencesMutation,
  useUploadAvatarMutation,
  useChangePasswordMutation,
} = profileApi
